import { Gauge, Globe, Loader2, Play, RefreshCw, RotateCw, Square, Trash2, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { availableActions, isDestructive, type AppAction } from '@/lib/appStatus';
import { canChangeDomain, isSwitchOverlap } from '@/lib/domainSwitch';
import { cn } from '@/lib/utils';
import type { App } from '@/types/app';
import type { DomainSummary, DomainSwitch } from '@/types/domain';

const ACTION_META: Record<AppAction, { label: string; icon: LucideIcon }> = {
  scale: { label: 'Scale', icon: Gauge },
  stop: { label: 'Stop', icon: Square },
  start: { label: 'Start', icon: Play },
  redeploy: { label: 'Redeploy', icon: RotateCw },
  'change-domain': { label: 'Change domain', icon: Globe },
  delete: { label: 'Delete', icon: Trash2 },
};

interface AppHeaderActionsProps {
  app: App;
  onAction: (action: AppAction, app: App) => void;
  isBusy: (appId: number, action?: AppAction) => boolean;
  onRefresh: () => void;
  /** undefined while loading: Change domain stays hidden until known. */
  domains: DomainSummary[] | undefined;
  /** The app's latest domain switch; null when none, undefined while loading. */
  domainSwitch: DomainSwitch | null | undefined;
}

/** Refresh plus the actions valid for the app's status; labels collapse to icons below `sm`. */
export function AppHeaderActions({ app, onAction, isBusy, onRefresh, domains, domainSwitch }: AppHeaderActionsProps) {
  // Change domain needs to know the domains and the latest switch; one domain (or none) hides it.
  const offerDomain = domains !== undefined && domainSwitch !== undefined && domains.length > 1;
  const actions = availableActions(app.status).filter((a) => a !== 'change-domain' || offerDomain);
  const domainCheck = canChangeDomain(app, domains ?? [], domainSwitch);
  const graceUntil =
    domainSwitch?.status === 'awaiting_cleanup' && domainSwitch.grace_until ? Date.parse(domainSwitch.grace_until) : NaN;
  const domainReason = domainCheck.ok
    ? null
    : Number.isNaN(graceUntil)
      ? domainCheck.reason
      : `${domainCheck.reason} (until ${new Date(graceUntil).toLocaleString()})`;
  // While both hostnames are routed the server answers 409 APP_BUSY to every other operation.
  const switching = isSwitchOverlap(domainSwitch);
  const progress = switching
    ? 'Domain change in progress…'
    : actions.length > 0
      ? null
      : app.status === 'migrating'
        ? 'Migration in progress…'
        : app.status === 'pending'
          ? 'Deployment in progress…'
          : null;

  return (
    <>
      <Button type="button" variant="ghost" size="icon" aria-label="Refresh app" title="Refresh app" onClick={onRefresh}>
        <RefreshCw className="size-4" aria-hidden />
      </Button>
      {/* Always mounted so a change of the text is announced. */}
      <span role="status" className={cn('text-sm text-muted-foreground', !progress && 'sr-only')}>
        {progress}
      </span>
      {actions.map((action) => {
        const { label, icon: Icon } = ACTION_META[action];
        const busy = isBusy(app.id, action);
        const blocked = action === 'change-domain' ? domainReason : switching ? 'A domain change is in progress' : null;
        const hintId = `app-${app.id}-${action}-blocked`;
        return (
          <span key={action} className="contents">
            <Button
              type="button"
              size="sm"
              variant={action === 'start' ? 'default' : 'outline'}
              aria-label={label}
              title={blocked ? `${label}: ${blocked}` : label}
              disabled={busy}
              // aria-disabled (not disabled) keeps a blocked action focusable so its reason is announced.
              aria-disabled={blocked !== null || undefined}
              aria-describedby={blocked ? hintId : undefined}
              className={cn(isDestructive(action) && 'text-destructive hover:text-destructive')}
              onClick={() => {
                if (blocked === null) onAction(action, app);
              }}
            >
              {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Icon className="size-4" aria-hidden />}
              <span className="hidden sm:inline">{label}</span>
            </Button>
            {blocked ? (
              <span id={hintId} className="sr-only">
                {blocked}
              </span>
            ) : null}
          </span>
        );
      })}
    </>
  );
}
